/* SPDX-License-Identifier: AGPL-3.0-or-later */

import {Component, OnInit, input} from '@angular/core';
import {KoStandingsEndpointService, TeamDto, KoStandingDto} from '@api';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {KnockoutPhaseTreeNodeComponent} from '../../knockout-phase-tree-node/knockout-phase-tree-node.component';
import {HttpErrorResponse} from '@angular/common/http';
import {RouterModule} from '@angular/router';
import {MatCardModule} from '@angular/material/card';
import {HeaderCardComponent} from '../../header-card/header-card.component';
import {MatButtonModule, MatIconButton} from '@angular/material/button';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectChange, MatSelectModule} from '@angular/material/select';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatIcon} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ConfirmationService} from 'src/app/services/confirmation.service';
import {catchError, firstValueFrom, of, tap} from 'rxjs';
import {CopyLinkService} from 'src/app/services/copy-link.service';
import {ThirdPlaceDialogComponent} from "../../third-place-dialog/third-place-dialog.component";
import {MatDialog, MatDialogModule} from "@angular/material/dialog";

@Component({
    selector: 'app-edit-knockout-phase-tree',
    standalone: true,
    templateUrl: './edit-knockout-phase-tree.component.html',
    styleUrl: './edit-knockout-phase-tree.component.scss',
    imports: [
        KnockoutPhaseTreeNodeComponent,
        MatProgressSpinnerModule,
        MatCardModule,
        RouterModule,
        HeaderCardComponent,
        MatButtonModule,
        MatFormFieldModule,
        MatSelectModule,
        MatDialogModule,
        MatInputModule,
        MatIcon,
        MatIconButton,
        MatTooltipModule,
        FormsModule,
        ReactiveFormsModule,
    ],
})
export class EditKnockoutPhaseTreeComponent implements OnInit {
    tournamentId = input<number>();

    tree?: KoStandingDto;

    get teams() {
        if (!this.tree) {
            return [];
        }

        const getTeamsFromTree = (node: KoStandingDto): TeamDto[] =>
            node.team
                ? [node.team, ...(node.preceedingStandings?.flatMap(getTeamsFromTree) ?? [])]
                : (node.preceedingStandings?.flatMap(getTeamsFromTree) ?? []);

        const map = new Map<number, TeamDto>();
        for (const team of getTeamsFromTree(this.tree)) {
            if (!team.id) {
                continue;
            }
            if (map.has(team.id)) {
                continue;
            }

            map.set(team.id, team);
        }

        return [...map.values()];
    }

    isInitialLoading = true;
    showErrorMessage = false;
    noStandingsFound = false;

    updateInProgress = false;

    readonly noWinnerYetValue = -1;

    constructor(
        private koStandingsEndpoint: KoStandingsEndpointService,
        private confirmService: ConfirmationService,
        private snackBar: MatSnackBar,
        private copyLinkService: CopyLinkService,
        private matDialog: MatDialog,
    ) {
    }

    ngOnInit() {
        this.loadData();
    }

    loadData = () => {
        if (this.tournamentId() === undefined) {
            return;
        }
        this.koStandingsEndpoint.getKoStandingsTree(this.tournamentId()!).subscribe({
            next: value => {
                this.isInitialLoading = false;
                this.showErrorMessage = false;
                this.noStandingsFound = false;
                this.updateInProgress = false;

                this.tree = value;
                this.sortTreeById(this.tree)
            },
            error: er => {
                if (er instanceof HttpErrorResponse) {
                    this.noStandingsFound = er.status === 404;
                }
                this.isInitialLoading = false;
                this.showErrorMessage = true;
                this.updateInProgress = false;
            },
        });
    };

    onChange = (matchId: number, e: MatSelectChange) => {
        if (!this.tree) {
            return [];
        }
        const [node] = this.findNode(this.tree, matchId);

        const teamId = Number.parseInt(e.value, 10);

        this.updateInProgress = true;
        if (teamId === this.noWinnerYetValue) {
            node.team = undefined;
            this.koStandingsEndpoint
                .updateKoStanding(this.tournamentId()!, node.id!, {
                    teamSet: {teamId: undefined},
                })
                .subscribe({
                    next: () => {
                        this.snackBar.open('Successfully updated KO standing.', 'Close', {duration: 3000});
                        return this.loadData();
                    },
                    error: () => {
                        this.snackBar.open('Failed to updated KO standing.', 'Close', {duration: 6000});
                    },
                });
        }

        const team = this.teams.find(t => t.id === teamId);
        if (!team) {
            return;
        }

        node.team = team;
        this.koStandingsEndpoint
            .updateKoStanding(this.tournamentId()!, node.id!, {
                teamSet: {teamId},
            })
            .subscribe({
                next: () => {
                    this.snackBar.open('Successfully updated KO standing.', 'Close', {duration: 3000});
                    return this.loadData();
                },
                error: () => {
                    this.snackBar.open('Failed to updated KO standing.', 'Close', {duration: 6000});
                },
            });
    };

    getTeamOptions = (matchId: number) => {
        if (!this.tree) {
            return [];
        }
        const [node] = this.findNode(this.tree, matchId);

        return node.preceedingStandings?.flatMap(n => (n.team ? [n.team] : [])) ?? [];
    };

    findNode = (node: KoStandingDto, matchId: number): KoStandingDto[] =>
        node.id === matchId
            ? [node]
            : (node.preceedingStandings?.flatMap(n => this.findNode(n, matchId)) ?? []);

    findParentNode = (node: KoStandingDto, matchId: number): KoStandingDto[] =>
        node.preceedingStandings?.some(n => n.id === matchId)
            ? [node]
            : (node.preceedingStandings?.flatMap(n => this.findParentNode(n, matchId)) ?? []);

    canSelectMatchWinner = (matchId: number) => {
        if (!this.tree) {
            return;
        }

        const [node] = this.findNode(this.tree, matchId);
        if (node.preceedingStandings?.some(n => !n.team) || node.startTime === null) {
            return true;
        }

        const [parentNode] = this.findParentNode(this.tree, matchId);
        if (parentNode?.team) {
            return true;
        }

        return false;
    };

    isFinalKoStanding(matchId: number): boolean {
        return this.tree !== undefined && this.tree.id === matchId;
    }

    nodeHasBothParticipants(node: KoStandingDto | undefined): boolean {
        if (node === undefined) {
            return false;
        }

        const participants = node.preceedingStandings?.filter(s => !!s.team).length || 0;
        return participants === 2;
    }

    teamHasPickedUpDrinksForMatch(matchId: number | undefined, team: TeamDto | undefined): boolean {
        if (this.tree === undefined || matchId === undefined || team?.id === undefined) {
            return false;
        }

        const [node] = this.findNode(this.tree, matchId);
        return node?.drinksCollected ?? false;
    }

    teamCanPickUpDrinksForMatch(matchId: number, team: TeamDto | undefined): boolean {
        if (this.tree === undefined || matchId === undefined || team?.id === undefined) {
            return false;
        }

        const [parentNode] = this.findParentNode(this.tree, matchId);
        const result =
            !this.teamHasPickedUpDrinksForMatch(matchId, team) &&
            !this.canSelectMatchWinner(matchId) &&
            this.nodeHasBothParticipants(parentNode);

        return result ?? false;
    }

    async markTeamDrinksPickedUp(matchId: number, team: TeamDto) {
        if (this.tournamentId() === undefined || team?.id == undefined) {
            console.log('got invalid tournament or participant');
            return;
        }

        const answer = await this.confirmService.openConfirmationDialog(
            `mark drinks as picked up for team '${team.name}?'`,
            'This action cannot be undone.',
        );

        if (answer) {
            firstValueFrom(
                this.koStandingsEndpoint
                    .updateKoStanding(this.tournamentId()!, matchId, {
                        drinksPickup: {teamId: team.id},
                    })
                    .pipe(
                        tap(() => {
                            this.snackBar.open(`Marked drinks for team ${team.name} as collected`, 'Close', {
                                duration: 2500,
                            });
                        }),
                        catchError(error => {
                            console.error(error);
                            const errorMessage =
                                error.error.error ??
                                (typeof error.error === 'string' ? error.error : error.message);
                            this.snackBar.open(
                                `Failed to mark drinks as collected for team: ${errorMessage}`,
                                'Close',
                                {duration: 4000},
                            );
                            return of([]);
                        }),
                    ),
            ).then(() => this.loadData());
        }
    }

    openCopyPublicLinkDialog() {
        const url = new URL(`#/infoscreen/${this.tournamentId()}/ko-phase`, window.location.href);
        this.copyLinkService.openDialog({
            link: url.href,
            title: 'Public link to ko phase',
        });
    }

    sortTreeById(tree: KoStandingDto) {
        if (!tree || !tree.preceedingStandings) return;
        tree.preceedingStandings.sort((a, b) => a.id! - b.id!);
        tree.preceedingStandings.forEach(node => this.sortTreeById(node));
    }

    openThirdPlaceDialog() {
        const ref = this.matDialog.open(ThirdPlaceDialogComponent, {
            width: '750px',
            maxWidth: '900px',
        });

        ref.componentInstance.tournamentId.set(this.tournamentId());
    }
}
